// src/hooks/useTVNavigation.js
// ─── Global TV D-pad Navigation Hook ────────────────────────────

import { useEffect, useCallback, useRef } from 'react';

function getRowCards(rowEl) {
  return Array.from(rowEl.querySelectorAll('[data-tv-card]'))
    .filter(el => !el.disabled && el.offsetParent !== null);
}

function scrollToFocused(el) {
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
}

// ── Grid navigation (Education / Management / Games pages) ────────
function navigateGrid(focused, key) {
  const grid = focused?.closest('[data-tv-grid]');
  if (!grid) return false;

  const cards = Array.from(grid.querySelectorAll('[data-tv-card]'))
    .filter(el => !el.disabled && el.offsetParent !== null);
  const idx = cards.indexOf(focused);
  if (idx === -1) return false;

  const cols = window.getComputedStyle(grid).gridTemplateColumns.split(' ').length || 1;

  let target = null;
  if (key === 'ArrowRight') target = cards[idx + 1] ?? null;
  if (key === 'ArrowLeft')  target = cards[idx - 1] ?? null;
  if (key === 'ArrowDown')  target = cards[idx + cols] ?? null;
  if (key === 'ArrowUp') {
    if (idx - cols >= 0) {
      target = cards[idx - cols];
    } else {
      const navEl = document.querySelector('[data-tv-nav]');
      navEl?.focus();
      return true;
    }
  }

  if (target) { target.focus(); scrollToFocused(target); }
  return true;
}

export function useTVNavigation({ enabled = true, onBack } = {}) {
  // ✅ Save last focused element — restore if Android resets focus on frame drop
  const lastFocusedRef = useRef(null);

  // Track focus changes
  useEffect(() => {
    if (!enabled) return;
    const onFocus = (e) => {
      const el = e.target;
      // Only save if it's our element
      if (el?.hasAttribute?.('data-tv-card') || el?.hasAttribute?.('data-tv-nav')) {
        lastFocusedRef.current = el;
      }
    };
    document.addEventListener('focusin', onFocus, true);
    return () => document.removeEventListener('focusin', onFocus, true);
  }, [enabled]);

  // ✅ Detect focus reset to body/null — restore last focused element
  useEffect(() => {
    if (!enabled) return;
    const onBlur = () => {
      // Small delay — Android TV sometimes resets focus after frame drop
      setTimeout(() => {
        const active = document.activeElement;
        if ((!active || active === document.body) && lastFocusedRef.current) {
          // Check element is still in DOM and visible
          const el = lastFocusedRef.current;
          if (document.contains(el) && el.offsetParent !== null) {
            el.focus({ preventScroll: true });
          }
        }
      }, 100);
    };
    document.addEventListener('blur', onBlur, true);
    return () => document.removeEventListener('blur', onBlur, true);
  }, [enabled]);

  const handleKeyDown = useCallback((e) => {
    if (!enabled) return;

    const focused = document.activeElement;
    const key = e.key;

    // ── Enter / OK ────────────────────────────────────────────
    if (key === 'Enter') {
      if (focused && focused !== document.body) {
        e.preventDefault();
        focused.click();
        return;
      }
    }

    // ── Space (non-input only) ────────────────────────────────
    if (key === ' ') {
      const tag = focused?.tagName?.toLowerCase();
      if (tag !== 'input' && tag !== 'textarea') {
        e.preventDefault();
        focused?.click();
        return;
      }
    }

    // ── Back / Escape ─────────────────────────────────────────
    if (key === 'Escape' || key === 'GoBack' || key === 'XF86Back') {
      e.preventDefault();
      if (onBack) onBack();
      return;
    }

    // ── Arrow keys only ───────────────────────────────────────
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) return;
    e.preventDefault();

    // ── Grid navigation (Education / Management / Games) ──────
    if (focused?.closest('[data-tv-grid]')) {
      navigateGrid(focused, key);
      return;
    }

    // ── Horizontal row navigation (All.js rows) ───────────────
    const parentRow = focused?.closest('[data-tv-row]');
    if (parentRow) {
      const rowCards = getRowCards(parentRow);
      const idx = rowCards.indexOf(focused);

      if (key === 'ArrowRight') {
        const next = rowCards[idx + 1];
        if (next) { next.focus(); scrollToFocused(next); }
        return;
      }
      if (key === 'ArrowLeft') {
        const prev = rowCards[idx - 1];
        if (prev) { prev.focus(); scrollToFocused(prev); }
        return;
      }

      if (key === 'ArrowUp' || key === 'ArrowDown') {
        const allRows = Array.from(
          document.querySelectorAll('[data-tv-row], [data-tv-section]')
        ).filter(el => el.offsetParent !== null);

        const rowIdx = allRows.indexOf(parentRow);

        if (key === 'ArrowUp') {
          if (rowIdx > 0) {
            const targetCards = getRowCards(allRows[rowIdx - 1]);
            const target = targetCards[Math.min(idx, targetCards.length - 1)] || targetCards[0];
            if (target) { target.focus(); scrollToFocused(target); }
          } else {
            const navEl = document.querySelector('[data-tv-nav]');
            navEl?.focus();
          }
        }
        if (key === 'ArrowDown') {
          if (rowIdx < allRows.length - 1) {
            const targetCards = getRowCards(allRows[rowIdx + 1]);
            const target = targetCards[Math.min(idx, targetCards.length - 1)] || targetCards[0];
            if (target) { target.focus(); scrollToFocused(target); }
          }
        }
        return;
      }
    }

    // ── Navbar navigation ─────────────────────────────────────
    const isNavItem = focused?.hasAttribute('data-tv-nav');
    if (isNavItem) {
      const navItems = Array.from(document.querySelectorAll('[data-tv-nav]'))
        .filter(el => el.offsetParent !== null);
      const idx = navItems.indexOf(focused);

      if (key === 'ArrowRight') {
        const next = navItems[idx + 1] || navItems[0];
        next?.focus();
        return;
      }
      if (key === 'ArrowLeft') {
        const prev = navItems[idx - 1] || navItems[navItems.length - 1];
        prev?.focus();
        return;
      }
      if (key === 'ArrowDown') {
        const firstCard = document.querySelector('[data-tv-card]');
        if (firstCard) { firstCard.focus(); scrollToFocused(firstCard); }
        return;
      }
      return;
    }

    // ── Nothing focused — focus first element ─────────────────
    if (!focused || focused === document.body) {
      const first = document.querySelector('[data-tv-nav], [data-tv-card]');
      if (first) { first.focus(); scrollToFocused(first); }
    }

  }, [enabled, onBack]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

export default useTVNavigation;